import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Groups as GroupsIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

interface Tanda {
  id: number;
  name: string;
  description: string;
  total_amount: number;
  member_count: number;
  contribution_amount: number;
  frequency: string;
  status: string;
  start_date: string;
  current_members: number;
  organizer: {
    id: number;
    username: string;
    full_name: string;
  };
}

interface TandaMember {
  id: number;
  user_id: number;
  position: number;
  joined_at: string;
  user: {
    id: number;
    username: string;
    full_name: string;
    email: string;
  };
}

interface TandaRound {
  id: number;
  round_number: number;
  due_date: string;
  completed_at: string | null;
  recipient: {
    id: number;
    username: string;
    full_name: string;
  };
}

interface Payment {
  id: number;
  amount: number;
  status: string;
  payment_date: string | null;
  user: {
    id: number;
    username: string;
    full_name: string;
  };
}

const TandaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [tanda, setTanda] = useState<Tanda | null>(null);
  const [members, setMembers] = useState<TandaMember[]>([]);
  const [rounds, setRounds] = useState<TandaRound[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [isOrganizer, setIsOrganizer] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTandaDetails();
    }
  }, [id]);

  const fetchTandaDetails = async () => {
    try {
      setLoading(true);

      // Fetch tanda details
      const tandaResponse = await axios.get(`/tandas/${id}`);
      setTanda(tandaResponse.data);

      // Check if user is organizer
      setIsOrganizer(tandaResponse.data.organizer.id === user?.id);

      // Fetch members
      const membersResponse = await axios.get(`/tandas/${id}/members`);
      setMembers(membersResponse.data);

      // Check if user is a member
      const userIsMember = membersResponse.data.some((member: TandaMember) =>
        member.user_id === user?.id
      );
      setIsMember(userIsMember);

      // Fetch rounds if tanda is active or completed
      if (tandaResponse.data.status === 'active' || tandaResponse.data.status === 'completed') {
        const roundsResponse = await axios.get(`/tandas/${id}/rounds`);
        setRounds(roundsResponse.data);

        // Fetch payments for each round
        const paymentsPromises = roundsResponse.data.map((round: TandaRound) =>
          axios.get(`/rounds/${round.id}/payments`)
        );
        const paymentsResponses = await Promise.all(paymentsPromises);
        const allPayments = paymentsResponses.flatMap(response => response.data);
        setPayments(allPayments);
      }
    } catch (error) {
      console.error('Failed to fetch tanda details:', error);
      enqueueSnackbar('Failed to load tanda details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTanda = async () => {
    try {
      await axios.post(`/tandas/${id}/members/me`);
      enqueueSnackbar('Successfully joined tanda!', { variant: 'success' });
      fetchTandaDetails();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to join tanda', { variant: 'error' });
    }
  };

  const handleLeaveTanda = async () => {
    try {
      await axios.delete(`/tandas/${id}/members/me`);
      enqueueSnackbar('Successfully left tanda', { variant: 'success' });
      navigate('/tandas');
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to leave tanda', { variant: 'error' });
    }
  };

  const handleStartTanda = async () => {
    try {
      await axios.post(`/tandas/${id}/start`);
      enqueueSnackbar('Tanda started successfully!', { variant: 'success' });
      setStartDialogOpen(false);
      fetchTandaDetails();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to start tanda', { variant: 'error' });
    }
  };

  const handleDeleteTanda = async () => {
    try {
      await axios.delete(`/tandas/${id}`);
      enqueueSnackbar('Tanda deleted successfully', { variant: 'success' });
      navigate('/tandas');
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to delete tanda', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'forming': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircleIcon color="success" />;
      case 'forming': return <WarningIcon color="warning" />;
      case 'completed': return <InfoIcon color="info" />;
      case 'cancelled': return <ErrorIcon color="error" />;
      default: return undefined;
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'weekly': return t('common.weekly');
      case 'biweekly': return t('common.biweekly');
      case 'monthly': return t('common.monthly');
      default: return frequency;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'late': return 'error';
      default: return 'default';
    }
  };

  const canJoin = () => {
    return !isMember && tanda?.status === 'forming' && tanda.current_members < tanda.member_count;
  };

  const canStart = () => {
    return isOrganizer && tanda?.status === 'forming' && tanda.current_members === tanda.member_count;
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!tanda) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          Tanda not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tandas')}
          sx={{ mb: 2 }}
        >
          {t('tandaDetail.backToTandas')}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {tanda.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={getStatusIcon(tanda.status)}
                label={tanda.status.toUpperCase()}
                color={getStatusColor(tanda.status) as any}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {t('tandaDetail.organizedBy')} {tanda.organizer.full_name}
              </Typography>
            </Box>
          </Box>

          <Box>
            <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
            >
              {isOrganizer && tanda.status === 'forming' && (
                <MenuItem onClick={() => { setMenuAnchorEl(null); setStartDialogOpen(true); }}>
                  <ListItemIcon>
                    <PlayArrowIcon fontSize="small" />
                  </ListItemIcon>
                  {t('tandaDetail.startTanda')}
                </MenuItem>
              )}
              {isOrganizer && tanda.status === 'forming' && (
                <MenuItem onClick={() => { setMenuAnchorEl(null); navigate(`/tandas/${id}/edit`); }}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  {t('tandaDetail.editTanda')}
                </MenuItem>
              )}
              {isOrganizer && (
                <MenuItem onClick={() => { setMenuAnchorEl(null); setDeleteDialogOpen(true); }}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  {t('tandaDetail.deleteTanda')}
                </MenuItem>
              )}
              {isMember && !isOrganizer && (
                <MenuItem onClick={() => { setMenuAnchorEl(null); handleLeaveTanda(); }}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  {t('tandaDetail.leaveTanda')}
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GroupsIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {tanda.current_members}/{tanda.member_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tandaDetail.members')}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(tanda.current_members / tanda.member_count) * 100}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4">${tanda.contribution_amount} MXN</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tandaDetail.contribution')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon color="warning" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4">${tanda.total_amount} MXN</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tandaDetail.totalPot')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon color="info" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4">{getFrequencyLabel(tanda.frequency)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tandaDetail.frequency')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canJoin() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleJoinTanda}
            >
              {t('tandaDetail.joinTanda')}
            </Button>
          )}

          {isMember && tanda.status === 'active' && (
            <Button
              variant="outlined"
              startIcon={<MoneyIcon />}
              onClick={() => setTabValue(2)}
            >
              {t('tandaDetail.makePayment')}
            </Button>
          )}

          {canStart() && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={() => setStartDialogOpen(true)}
            >
              {t('tandaDetail.startTanda')}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Description and Info */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('tandaDetail.description')}
            </Typography>
            <Typography variant="body1" paragraph>
              {tanda.description || t('tandaDetail.noDescription')}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('tandaDetail.startDate')}
                </Typography>
                <Typography variant="body1">
                  {format(new Date(tanda.start_date), 'MMMM dd, yyyy')}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('tandaDetail.duration')}
                </Typography>
                <Typography variant="body1">
                  {tanda.member_count} {tanda.frequency === 'monthly' ? t('tandaDetail.months') : t('tandaDetail.months')}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('tandaDetail.organizer')}
                </Typography>
                <Typography variant="body1">
                  {tanda.organizer.full_name}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('tandaDetail.created')}
                </Typography>
                <Typography variant="body1">
                  {format(new Date(tanda.start_date), 'MMMM dd, yyyy')}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabs */}
          <Paper>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant="fullWidth"
            >
              <Tab label={t('tandaDetail.membersTab')} />
              <Tab label={t('tandaDetail.roundsTab')} />
              <Tab label={t('tandaDetail.paymentsTab')} />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {tabValue === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('tandaDetail.members')} ({members.length})
                  </Typography>
                  <List>
                    {members.map((member) => (
                      <ListItem
                        key={member.id}
                        secondaryAction={
                          <Chip
                            label={`${t('tandaDetail.position')} ${member.position}`}
                            size="small"
                            color={member.position === 1 ? 'primary' : 'default'}
                          />
                        }
                      >
                        <ListItemIcon>
                          <PersonIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={member.user.full_name}
                          secondary={`${t('tandaDetail.joined')} ${format(new Date(member.joined_at), 'MMM dd, yyyy')}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('tandaDetail.roundsTab')} ({rounds.length})
                  </Typography>
                  {rounds.length === 0 ? (
                    <Alert severity="info">
                      {t('tandaDetail.notStarted')}
                    </Alert>
                  ) : (
                    <List>
                      {rounds.map((round) => (
                        <ListItem
                          key={round.id}
                          secondaryAction={
                            <Chip
                              label={round.completed_at ? t('tandaDetail.completed') : t('tandaDetail.pending')}
                              color={round.completed_at ? 'success' : 'warning'}
                              size="small"
                            />
                          }
                        >
                          <ListItemIcon>
                            <CalendarIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${t('tandaDetail.round')} ${round.round_number}: ${round.recipient.full_name}`}
                            secondary={`${t('tandaDetail.due')} ${format(new Date(round.due_date), 'MMM dd, yyyy')}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('tandaDetail.paymentHistory')}
                  </Typography>
                  {payments.length === 0 ? (
                    <Alert severity="info">
                      {t('tandaDetail.noPayments')}
                    </Alert>
                  ) : (
                    <List>
                      {payments.map((payment) => (
                        <ListItem
                          key={payment.id}
                          secondaryAction={
                            <Chip
                              label={payment.status}
                              color={getPaymentStatusColor(payment.status) as any}
                              size="small"
                            />
                          }
                        >
                          <ListItemIcon>
                            <MoneyIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${payment.user.full_name} - $${payment.amount} MXN`}
                            secondary={
                              payment.payment_date
                                ? `${t('tandaDetail.paidOn')} ${format(new Date(payment.payment_date), 'MMM dd, yyyy')}`
                                : t('tandaDetail.notPaidYet')
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Status and Actions */}
        <Grid item xs={12} md={4}>
          {/* Status Card */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('tandaDetail.tandaStatus')}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('tandaDetail.progress')}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  tanda.status === 'completed' ? 100 :
                  tanda.status === 'active' ? 50 :
                  (tanda.current_members / tanda.member_count) * 100
                }
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {tanda.status === 'forming' && `${tanda.current_members} / ${tanda.member_count} ${t('tandaDetail.membersJoined')}`}
                {tanda.status === 'active' && t('tandaDetail.inProgress')}
                {tanda.status === 'completed' && t('tandaDetail.tandaCompleted')}
                {tanda.status === 'cancelled' && t('tandaDetail.tandaCancelled')}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('tandaDetail.nextSteps')}
            </Typography>
            <List dense>
              {tanda.status === 'forming' && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <GroupsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('tandaDetail.inviteMembers')}
                      secondary={`${tanda.member_count - tanda.current_members} ${t('tandaDetail.spotsRemaining')}`}
                    />
                  </ListItem>
                  {canStart() && (
                    <ListItem>
                      <ListItemIcon>
                        <PlayArrowIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('tandaDetail.startTandaStep')}
                        secondary={t('tandaDetail.allSpotsFilled')}
                      />
                    </ListItem>
                  )}
                </>
              )}

              {tanda.status === 'active' && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <MoneyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('tandaDetail.makePaymentsStep')}
                      secondary={t('tandaDetail.dueAccordingSchedule')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('tandaDetail.trackRounds')}
                      secondary={t('tandaDetail.monitorFunds')}
                    />
                  </ListItem>
                </>
              )}
            </List>
          </Paper>

          {/* Rules Card */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('tandaDetail.tandaRules')}
            </Typography>
            <Typography variant="body2" paragraph>
              • {t('tandaDetail.ruleContribute')} ${tanda.contribution_amount} MXN {getFrequencyLabel(tanda.frequency).toLowerCase()}
              <br />
              • {t('tandaDetail.ruleReceive')} ${tanda.total_amount} MXN {t('tandaDetail.eachRound')}
              <br />
              • {t('tandaDetail.ruleOrder')}
              <br />
              • {t('tandaDetail.ruleOnTime')}
              <br />
              • {t('tandaDetail.ruleLate')}
              <br />
              • {t('tandaDetail.ruleOrganizer')}
            </Typography>
          </Paper>

          {/* Share Card */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('tandaDetail.shareTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('tandaDetail.shareText')}
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                enqueueSnackbar(t('tandaDetail.linkCopied'), { variant: 'success' });
              }}
            >
              {t('tandaDetail.copyLink')}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Start Tanda Dialog */}
      <Dialog open={startDialogOpen} onClose={() => setStartDialogOpen(false)}>
        <DialogTitle>{t('tandaDetail.startDialogTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('tandaDetail.startDialogWarning')}</strong>
              <br />
              • {t('tandaDetail.startDialogWarning1')}
              <br />
              • {t('tandaDetail.startDialogWarning2')}
              <br />
              • {t('tandaDetail.startDialogWarning3')}
            </Typography>
          </Alert>
          <Typography variant="body2" paragraph>
            {t('tandaDetail.startDialogText')} {format(new Date(tanda.start_date), 'MMMM dd, yyyy')}.
            {t('tandaDetail.startDialogText2')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tandaDetail.startDialogConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDialogOpen(false)}>{t('tandaDetail.cancel')}</Button>
          <Button onClick={handleStartTanda} variant="contained" color="success">
            {t('tandaDetail.startTanda')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Tanda Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('tandaDetail.deleteDialogTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('tandaDetail.deleteDialogWarning')}</strong>
            </Typography>
          </Alert>
          <Typography variant="body2" paragraph>
            {t('tandaDetail.deleteDialogText')} "{tanda.name}" {t('tandaDetail.deleteDialogText2')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tandaDetail.deleteDialogConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('tandaDetail.cancel')}</Button>
          <Button onClick={handleDeleteTanda} variant="contained" color="error">
            {t('tandaDetail.deleteTanda')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TandaDetail;
